import {
  tenantAdminClinicianOperationsContractVersion,
  tenantAdminClinicianOperationsRoutePath,
  tenantAdminCommandContractVersion,
  tenantAdminCommandRoutePath,
  tenantAdminOutboundCallsCampaignsContractVersion,
  tenantAdminOutboundCallsCampaignsRoutePath,
  tenantAdminOutboundCallsCommandContractVersion,
  tenantAdminOutboundCallsCommandRoutePath,
  tenantAdminOutboundCallsQuestionnairesContractVersion,
  tenantAdminOutboundCallsQuestionnairesRoutePath,
  tenantAdminOutboundCallsSummaryContractVersion,
  tenantAdminOutboundCallsSummaryRoutePath,
  tenantAdminOutboundCallsTargetsContractVersion,
  tenantAdminOutboundCallsTargetsRoutePath,
  tenantAdminPatientOperationsContractVersion,
  tenantAdminPatientOperationsRoutePath,
  tenantAdminReviewQueueItemsContractVersion,
  tenantAdminReviewQueueItemsRoutePath,
  tenantAdminSummaryContractVersion,
  tenantAdminSummaryRoutePath,
  type TenantAdminClinicianOperationsRequestV1,
  type TenantAdminClinicianOperationsResponseV1,
  type TenantAdminCommandInputV1,
  type TenantAdminCommandResponseV1,
  type TenantAdminOutboundCallCommandInputV1,
  type TenantAdminOutboundCallsCampaignsRequestV1,
  type TenantAdminOutboundCallsCampaignsResponseV1,
  type TenantAdminOutboundCallsCommandResponseV1,
  type TenantAdminOutboundCallsQuestionnairesRequestV1,
  type TenantAdminOutboundCallsQuestionnairesResponseV1,
  type TenantAdminOutboundCallsSummaryRequestV1,
  type TenantAdminOutboundCallsSummaryResponseV1,
  type TenantAdminOutboundCallsTargetsRequestV1,
  type TenantAdminOutboundCallsTargetsResponseV1,
  type TenantAdminPatientOperationsRequestV1,
  type TenantAdminPatientOperationsResponseV1,
  type TenantAdminReviewQueueItemsRequestV1,
  type TenantAdminReviewQueueItemsResponseV1,
  type TenantAdminSummaryRequestV1,
  type TenantAdminSummaryResponseV1,
} from "@amplecare/contracts";

import type { MobileRuntimeConfig } from "../config/runtime-config.js";

function resolveSessionFetchBaseUrl(config: MobileRuntimeConfig): string {
  if (config.sessionApiBaseUrl) {
    return config.sessionApiBaseUrl;
  }

  if (
    typeof window !== "undefined" &&
    typeof window.location?.origin === "string" &&
    window.location.origin.length > 0
  ) {
    return window.location.origin;
  }

  throw new Error("tenant-admin-console-session-base-url-unavailable");
}

function buildTenantAdminSummaryUrl(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminSummaryRequestV1, "tenantId"> = {},
): string {
  const url = new URL(
    tenantAdminSummaryRoutePath,
    resolveSessionFetchBaseUrl(config),
  );

  url.searchParams.set("tenantId", config.requestTenantId);

  if (request.analyticsStartAt) {
    url.searchParams.set("analyticsStartAt", request.analyticsStartAt);
  }

  if (request.analyticsEndAt) {
    url.searchParams.set("analyticsEndAt", request.analyticsEndAt);
  }

  if (request.analyticsDepartmentCode) {
    url.searchParams.set(
      "analyticsDepartmentCode",
      request.analyticsDepartmentCode,
    );
  }

  return url.toString();
}

function buildTenantAdminCommandUrl(config: MobileRuntimeConfig): string {
  return new URL(
    tenantAdminCommandRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildTenantAdminOutboundCallsSummaryUrl(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminOutboundCallsSummaryRequestV1, "tenantId"> = {},
): string {
  const url = new URL(
    tenantAdminOutboundCallsSummaryRoutePath,
    resolveSessionFetchBaseUrl(config),
  );

  url.searchParams.set("tenantId", config.requestTenantId);

  if (request.campaignId) {
    url.searchParams.set("campaignId", request.campaignId);
  }

  if (request.analyticsStartAt) {
    url.searchParams.set("analyticsStartAt", request.analyticsStartAt);
  }

  if (request.analyticsEndAt) {
    url.searchParams.set("analyticsEndAt", request.analyticsEndAt);
  }

  if (request.generatedAt) {
    url.searchParams.set("generatedAt", request.generatedAt);
  }

  return url.toString();
}

function buildTenantAdminOutboundCallsCampaignsUrl(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminOutboundCallsCampaignsRequestV1, "tenantId"> = {},
): string {
  const url = new URL(
    tenantAdminOutboundCallsCampaignsRoutePath,
    resolveSessionFetchBaseUrl(config),
  );

  url.searchParams.set("tenantId", config.requestTenantId);

  if (request.status) {
    url.searchParams.set("status", request.status);
  }

  if (typeof request.offset === "number") {
    url.searchParams.set("offset", String(request.offset));
  }

  if (typeof request.limit === "number") {
    url.searchParams.set("limit", String(request.limit));
  }

  return url.toString();
}

function buildTenantAdminOutboundCallsQuestionnairesUrl(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminOutboundCallsQuestionnairesRequestV1, "tenantId"> = {},
): string {
  const url = new URL(
    tenantAdminOutboundCallsQuestionnairesRoutePath,
    resolveSessionFetchBaseUrl(config),
  );

  url.searchParams.set("tenantId", config.requestTenantId);

  if (request.status) {
    url.searchParams.set("status", request.status);
  }

  if (typeof request.includeQuestions === "boolean") {
    url.searchParams.set(
      "includeQuestions",
      request.includeQuestions ? "true" : "false",
    );
  }

  if (typeof request.offset === "number") {
    url.searchParams.set("offset", String(request.offset));
  }

  if (typeof request.limit === "number") {
    url.searchParams.set("limit", String(request.limit));
  }

  return url.toString();
}

function buildTenantAdminOutboundCallsTargetsUrl(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminOutboundCallsTargetsRequestV1, "tenantId"> = {},
): string {
  const url = new URL(
    tenantAdminOutboundCallsTargetsRoutePath,
    resolveSessionFetchBaseUrl(config),
  );

  url.searchParams.set("tenantId", config.requestTenantId);

  if (request.campaignId) {
    url.searchParams.set("campaignId", request.campaignId);
  }

  if (request.status) {
    url.searchParams.set("status", request.status);
  }

  if (request.patientId) {
    url.searchParams.set("patientId", request.patientId);
  }

  if (typeof request.offset === "number") {
    url.searchParams.set("offset", String(request.offset));
  }

  if (typeof request.limit === "number") {
    url.searchParams.set("limit", String(request.limit));
  }

  return url.toString();
}

function buildTenantAdminOutboundCallsCommandUrl(
  config: MobileRuntimeConfig,
): string {
  return new URL(
    tenantAdminOutboundCallsCommandRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildTenantAdminPatientOperationsUrl(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminPatientOperationsRequestV1, "tenantId"> = {},
): string {
  const url = new URL(
    tenantAdminPatientOperationsRoutePath,
    resolveSessionFetchBaseUrl(config),
  );

  url.searchParams.set("tenantId", config.requestTenantId);

  if (request.searchQuery?.trim()) {
    url.searchParams.set("search", request.searchQuery.trim());
  }

  if (typeof request.offset === "number") {
    url.searchParams.set("offset", String(request.offset));
  }

  if (typeof request.limit === "number") {
    url.searchParams.set("limit", String(request.limit));
  }

  if (request.sortKey) {
    url.searchParams.set("sortKey", request.sortKey);
  }

  if (request.sortDirection) {
    url.searchParams.set("sortDirection", request.sortDirection);
  }

  if (request.careTypeFilter) {
    url.searchParams.set("careTypeFilter", request.careTypeFilter);
  }

  if (typeof request.includeArchived === "boolean") {
    url.searchParams.set(
      "includeArchived",
      request.includeArchived ? "true" : "false",
    );
  }

  if (request.lifecycleFilter) {
    url.searchParams.set("lifecycleFilter", request.lifecycleFilter);
  }

  return url.toString();
}

function buildTenantAdminClinicianOperationsUrl(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminClinicianOperationsRequestV1, "tenantId"> = {},
): string {
  const url = new URL(
    tenantAdminClinicianOperationsRoutePath,
    resolveSessionFetchBaseUrl(config),
  );

  url.searchParams.set("tenantId", config.requestTenantId);

  if (request.searchQuery?.trim()) {
    url.searchParams.set("search", request.searchQuery.trim());
  }

  if (typeof request.offset === "number") {
    url.searchParams.set("offset", String(request.offset));
  }

  if (typeof request.limit === "number") {
    url.searchParams.set("limit", String(request.limit));
  }

  if (request.sortKey) {
    url.searchParams.set("sortKey", request.sortKey);
  }

  if (request.sortDirection) {
    url.searchParams.set("sortDirection", request.sortDirection);
  }

  if (typeof request.includeInactive === "boolean") {
    url.searchParams.set(
      "includeInactive",
      request.includeInactive ? "true" : "false",
    );
  }

  return url.toString();
}

function buildTenantAdminReviewQueueItemsUrl(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminReviewQueueItemsRequestV1, "tenantId">,
): string {
  const url = new URL(
    tenantAdminReviewQueueItemsRoutePath,
    resolveSessionFetchBaseUrl(config),
  );

  url.searchParams.set("tenantId", config.requestTenantId);
  url.searchParams.set("queueId", request.queueId);

  if (typeof request.offset === "number") {
    url.searchParams.set("offset", String(request.offset));
  }

  if (typeof request.limit === "number") {
    url.searchParams.set("limit", String(request.limit));
  }

  return url.toString();
}

export async function loadTenantAdminSummary(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminSummaryRequestV1, "tenantId"> = {},
  signal?: AbortSignal,
): Promise<TenantAdminSummaryResponseV1> {
  const response = await fetch(buildTenantAdminSummaryUrl(config, request), {
    credentials: "include",
    headers: {
      accept: "application/json",
    },
    method: "GET",
    signal,
  });

  if (!response.ok) {
    throw new Error(`tenant-admin-console-summary-failed-${response.status}`);
  }

  const payload = (await response.json()) as TenantAdminSummaryResponseV1;

  if (
    payload.schemaVersion !== tenantAdminSummaryContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error("tenant-admin-console-summary-schema-mismatch");
  }

  return payload;
}

export async function loadTenantAdminReviewQueueItems(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminReviewQueueItemsRequestV1, "tenantId">,
  signal?: AbortSignal,
): Promise<TenantAdminReviewQueueItemsResponseV1> {
  const response = await fetch(
    buildTenantAdminReviewQueueItemsUrl(config, request),
    {
      credentials: "include",
      headers: {
        accept: "application/json",
      },
      method: "GET",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `tenant-admin-console-review-queue-items-failed-${response.status}`,
    );
  }

  const payload =
    (await response.json()) as TenantAdminReviewQueueItemsResponseV1;

  if (
    payload.schemaVersion !== tenantAdminReviewQueueItemsContractVersion ||
    payload.tenantId !== config.requestTenantId ||
    payload.queueId !== request.queueId
  ) {
    throw new Error("tenant-admin-console-review-queue-items-schema-mismatch");
  }

  return payload;
}

export async function loadTenantAdminPatientOperations(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminPatientOperationsRequestV1, "tenantId"> = {},
  signal?: AbortSignal,
): Promise<TenantAdminPatientOperationsResponseV1> {
  const response = await fetch(
    buildTenantAdminPatientOperationsUrl(config, request),
    {
      credentials: "include",
      headers: {
        accept: "application/json",
      },
      method: "GET",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `tenant-admin-console-patient-operations-failed-${response.status}`,
    );
  }

  const payload =
    (await response.json()) as TenantAdminPatientOperationsResponseV1;

  if (
    payload.schemaVersion !== tenantAdminPatientOperationsContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error("tenant-admin-console-patient-operations-schema-mismatch");
  }

  return payload;
}

export async function loadTenantAdminClinicianOperations(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminClinicianOperationsRequestV1, "tenantId"> = {},
  signal?: AbortSignal,
): Promise<TenantAdminClinicianOperationsResponseV1> {
  const response = await fetch(
    buildTenantAdminClinicianOperationsUrl(config, request),
    {
      credentials: "include",
      headers: {
        accept: "application/json",
      },
      method: "GET",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `tenant-admin-console-clinician-operations-failed-${response.status}`,
    );
  }

  const payload =
    (await response.json()) as TenantAdminClinicianOperationsResponseV1;

  if (
    payload.schemaVersion !== tenantAdminClinicianOperationsContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error("tenant-admin-console-clinician-operations-schema-mismatch");
  }

  return payload;
}

export async function loadTenantAdminOutboundCallsSummary(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminOutboundCallsSummaryRequestV1, "tenantId"> = {},
  signal?: AbortSignal,
): Promise<TenantAdminOutboundCallsSummaryResponseV1> {
  const response = await fetch(
    buildTenantAdminOutboundCallsSummaryUrl(config, request),
    {
      credentials: "include",
      headers: { accept: "application/json" },
      method: "GET",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `tenant-admin-console-outbound-summary-failed-${response.status}`,
    );
  }

  const payload =
    (await response.json()) as TenantAdminOutboundCallsSummaryResponseV1;

  if (
    payload.schemaVersion !== tenantAdminOutboundCallsSummaryContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error("tenant-admin-console-outbound-summary-schema-mismatch");
  }

  return payload;
}

export async function loadTenantAdminOutboundCallsCampaigns(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminOutboundCallsCampaignsRequestV1, "tenantId"> = {},
  signal?: AbortSignal,
): Promise<TenantAdminOutboundCallsCampaignsResponseV1> {
  const response = await fetch(
    buildTenantAdminOutboundCallsCampaignsUrl(config, request),
    {
      credentials: "include",
      headers: { accept: "application/json" },
      method: "GET",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `tenant-admin-console-outbound-campaigns-failed-${response.status}`,
    );
  }

  const payload =
    (await response.json()) as TenantAdminOutboundCallsCampaignsResponseV1;

  if (
    payload.schemaVersion !== tenantAdminOutboundCallsCampaignsContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error("tenant-admin-console-outbound-campaigns-schema-mismatch");
  }

  return payload;
}

export async function loadTenantAdminOutboundCallsQuestionnaires(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminOutboundCallsQuestionnairesRequestV1, "tenantId"> = {},
  signal?: AbortSignal,
): Promise<TenantAdminOutboundCallsQuestionnairesResponseV1> {
  const response = await fetch(
    buildTenantAdminOutboundCallsQuestionnairesUrl(config, request),
    {
      credentials: "include",
      headers: { accept: "application/json" },
      method: "GET",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `tenant-admin-console-outbound-questionnaires-failed-${response.status}`,
    );
  }

  const payload =
    (await response.json()) as TenantAdminOutboundCallsQuestionnairesResponseV1;

  if (
    payload.schemaVersion !==
      tenantAdminOutboundCallsQuestionnairesContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error(
      "tenant-admin-console-outbound-questionnaires-schema-mismatch",
    );
  }

  return payload;
}

export async function loadTenantAdminOutboundCallsTargets(
  config: MobileRuntimeConfig,
  request: Omit<TenantAdminOutboundCallsTargetsRequestV1, "tenantId"> = {},
  signal?: AbortSignal,
): Promise<TenantAdminOutboundCallsTargetsResponseV1> {
  const response = await fetch(
    buildTenantAdminOutboundCallsTargetsUrl(config, request),
    {
      credentials: "include",
      headers: { accept: "application/json" },
      method: "GET",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `tenant-admin-console-outbound-targets-failed-${response.status}`,
    );
  }

  const payload =
    (await response.json()) as TenantAdminOutboundCallsTargetsResponseV1;

  if (
    payload.schemaVersion !== tenantAdminOutboundCallsTargetsContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error("tenant-admin-console-outbound-targets-schema-mismatch");
  }

  return payload;
}

export async function submitTenantAdminOutboundCallCommand(
  config: MobileRuntimeConfig,
  command: TenantAdminOutboundCallCommandInputV1,
): Promise<TenantAdminOutboundCallsCommandResponseV1> {
  const response = await fetch(buildTenantAdminOutboundCallsCommandUrl(config), {
    body: JSON.stringify({
      command,
      schemaVersion: tenantAdminOutboundCallsCommandContractVersion,
      tenantId: config.requestTenantId,
    }),
    credentials: "include",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    method: "POST",
  });

  if (!response.ok) {
    let errorMessage = `tenant-admin-console-outbound-command-failed-${response.status}`;

    try {
      const errorPayload = (await response.json()) as { message?: unknown };

      if (typeof errorPayload.message === "string" && errorPayload.message.trim()) {
        errorMessage = errorPayload.message.trim();
      }
    } catch {
      // Preserve the status-based fallback when the server body is unavailable.
    }

    throw new Error(errorMessage);
  }

  const payload =
    (await response.json()) as TenantAdminOutboundCallsCommandResponseV1;

  if (
    payload.schemaVersion !== tenantAdminOutboundCallsCommandContractVersion ||
    payload.tenantId !== config.requestTenantId ||
    payload.summary.schemaVersion !== tenantAdminOutboundCallsSummaryContractVersion
  ) {
    throw new Error("tenant-admin-console-outbound-command-schema-mismatch");
  }

  return payload;
}

export async function submitTenantAdminCommand(
  config: MobileRuntimeConfig,
  command: TenantAdminCommandInputV1,
): Promise<TenantAdminCommandResponseV1> {
  const response = await fetch(buildTenantAdminCommandUrl(config), {
    body: JSON.stringify({
      schemaVersion: tenantAdminCommandContractVersion,
      tenantId: config.requestTenantId,
      command,
    }),
    credentials: "include",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    method: "POST",
  });

  if (!response.ok) {
    let errorMessage = `tenant-admin-console-command-failed-${response.status}`;

    try {
      const errorPayload = (await response.json()) as { message?: unknown };

      if (typeof errorPayload.message === "string" && errorPayload.message.trim()) {
        errorMessage = errorPayload.message.trim();
      }
    } catch {
      // Preserve the status-based fallback when the server body is unavailable.
    }

    throw new Error(errorMessage);
  }

  const payload = (await response.json()) as TenantAdminCommandResponseV1;

  if (
    payload.schemaVersion !== tenantAdminCommandContractVersion ||
    payload.tenantId !== config.requestTenantId ||
    payload.summary.schemaVersion !== tenantAdminSummaryContractVersion
  ) {
    throw new Error("tenant-admin-console-command-schema-mismatch");
  }

  return payload;
}
